import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/utilities/tailwind-utils";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { DashboardIcon } from "@/utilities/icon-utils";
import { useBreadcrumbOverrides } from "@/context/breadcrumb-context";
import { isNavigableModulePath } from "@/utilities/route-utils";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

const routeMap: Record<string, string> = {
  admin: "Administrator",
  practice: "Practice",
  professional: "Professional",
  application: "Application",
  dashboard: "Dashboard",
  profile: "Profile",
  lifecycle: "Lifecycle",
  analytics: "Analytics",
  projects: "Projects",
  team: "Team",
  settings: "Settings",
  help: "Help",
  search: "Search",
  reports: "Reports",
  applications: "Applications",
  users: "Users",
  system: "System",
  health: "Health",
  config: "Configuration",
  logs: "Logs",
  monitoring: "Monitoring",
  alerts: "Alerts",
  performance: "Performance",
  usage: "Usage",
  compliance: "Compliance",
  notifications: "Notifications",
  templates: "Templates",
  shifts: "Shifts",
  opportunity: "Opportunity",
  payments: "Payments",
  verification: "Verification",
  messaging: "Messaging"
};

export function NavBreadcrumb() {
  const location = useLocation();
  const { overrides } = useBreadcrumbOverrides();

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];

    if (pathSegments.length > 0) {
      pathSegments.forEach((segment, index) => {
        // Check for dynamic override first (e.g., GUID → "Shift Title")
        const overrideLabel = overrides.get(segment);
        const label =
          overrideLabel ||
          routeMap[segment] ||
          segment.charAt(0).toUpperCase() + segment.slice(1);
        const isLast = index === pathSegments.length - 1;

        if (isLast) {
          breadcrumbs.push({ label });
          return;
        }

        const href = "/" + pathSegments.slice(0, index + 1).join("/");

        // A crumb is a link only when its path is somewhere you can actually go.
        //
        // `/admin/applications/professional/{id}` walks through
        // `/admin/applications/professional`, which is not a route -- "professional"
        // there is a type discriminator, not a page. It was still rendered as a link, so
        // clicking the parent crumb navigated to a path that matches nothing and the
        // screen appeared not to respond.
        //
        // This is what the previous guard was reaching for. It read
        // `if (!(label in ["admin", "practice", "professional"]))`, and `in` on an array
        // tests index keys, not values -- so it was always true and excluded nothing. It
        // also compared the *label* ("Professional") against lowercase segment names,
        // which could never have matched even with the right operator.
        const [moduleName, ...rest] = pathSegments.slice(0, index + 1);
        const navigable = isNavigableModulePath(moduleName, "/" + rest.join("/"));

        breadcrumbs.push(navigable ? { label, href } : { label });
      });
    }

    return breadcrumbs;
  }, [location.pathname, overrides]);

  return (
    <div className="px-1">
      <Breadcrumb className="text-xs">
        <BreadcrumbList className="flex-wrap text-muted-foreground">
          {breadcrumbs.map((item, index) => (
            <div
              key={index}
              className={cn(
                "flex items-center",
                index < breadcrumbs.length - 1 && "hidden md:flex"
              )}
            >
              <BreadcrumbItem className="text-xs">
                {item.href ? (
                  <BreadcrumbLink
                    asChild
                    className="text-xs hover:text-foreground transition-colors"
                  >
                    <Link
                      to={item.href}
                      title={index === 0 ? "Dashboard" : item.label}
                    >
                      {index === 0 && breadcrumbs.length > 1 ? (
                        <DashboardIcon className="w-4 h-4" />
                      ) : index === 0 ? (
                        "Dashboard"
                      ) : (
                        item.label
                      )}
                    </Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage className="text-xs text-foreground font-medium">
                    {index === 0 ? "Dashboard" : item.label}
                  </BreadcrumbPage>
                )}
              </BreadcrumbItem>
              {index < breadcrumbs.length - 1 && (
                <BreadcrumbSeparator className="text-xs pl-2" />
              )}
            </div>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
}
